import { useEffect, useState } from "react";
import { ShoppingCart, CheckCircle2, PackageOpen, ShieldCheck, Wallet } from "lucide-react";
import Header from "@/components/Header";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const INR = (n) =>
  `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function ProductsPage() {
  const { user, refresh } = useAuth();
  const [products, setProducts] = useState([]);
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [buyingId, setBuyingId] = useState(null);

  const load = async () => {
    try {
      const [p, d] = await Promise.all([
        api.get("/products"),
        api.get("/dashboard"),
      ]);
      setProducts(p.data.items || []);
      setBalance(d.data.wallet.e_wallet);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const buy = async (product) => {
    if (buyingId) return;
    const confirmMsg = user?.activated
      ? `Buy "${product.name}" for ${INR(product.price)} from your E-Wallet?`
      : `Buy "${product.name}" for ${INR(product.price)}? This will ACTIVATE your account.`;
    if (!window.confirm(confirmMsg)) return;
    setBuyingId(product.id);
    try {
      const { data } = await api.post(`/products/${product.id}/purchase`);
      toast.success(
        data.activated_now
          ? "Purchase successful — your account is now ACTIVE!"
          : "Purchase successful"
      );
      await refresh();
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBuyingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100">
      <Header />
      <div style={{ background: "linear-gradient(180deg, #7f1d1d 0%, #991b1b 100%)" }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 text-center text-white">
          <h1 className="text-3xl font-extrabold tracking-tight"
              style={{ fontFamily: "Outfit, sans-serif" }}>
            Products
          </h1>
          <p className="text-white/85 mt-1">Buy any product to activate your ID &amp; unlock referral earnings</p>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 -mt-6 pb-14 space-y-5">
        <div className="grid grid-cols-2 gap-3">
          <div
            data-testid="user-activation-card"
            className={`rounded-2xl border p-4 flex items-center gap-3 shadow-sm ${
              user?.activated
                ? "bg-emerald-50 border-emerald-200"
                : "bg-amber-50 border-amber-200"
            }`}
          >
            <ShieldCheck
              size={22}
              className={user?.activated ? "text-emerald-600" : "text-amber-600"}
            />
            <div>
              <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
                Account Status
              </div>
              <div className={`font-bold ${user?.activated ? "text-emerald-700" : "text-amber-700"}`}>
                {user?.activated ? "Active" : "Inactive — buy a product to activate"}
              </div>
            </div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4 flex items-center gap-3 shadow-sm">
            <Wallet size={22} className="text-purple-600" />
            <div>
              <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
                E-Wallet
              </div>
              <div data-testid="products-ewallet-balance" className="font-bold text-slate-900">
                {INR(balance)}
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="py-14 flex justify-center">
            <div className="h-8 w-8 rounded-full border-4 border-red-600 border-t-transparent animate-spin" />
          </div>
        ) : products.length === 0 ? (
          <div data-testid="products-empty"
               className="p-10 text-center bg-white rounded-2xl border border-slate-200 shadow-sm">
            <PackageOpen size={40} className="mx-auto text-slate-300 mb-2" />
            <div className="font-semibold text-slate-700">No products yet</div>
            <div className="text-sm text-slate-500 mt-1">Please check back soon.</div>
          </div>
        ) : (
          <div data-testid="products-grid" className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {products.map((p) => {
              const canAfford = balance >= p.price;
              const busy = buyingId === p.id;
              return (
                <article
                  key={p.id}
                  data-testid={`product-card-${p.id}`}
                  className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col hover:shadow-md transition-shadow"
                >
                  <div className="aspect-[16/10] w-full bg-slate-100">
                    {p.image_url ? (
                      <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-400">
                        <PackageOpen size={40} />
                      </div>
                    )}
                  </div>
                  <div className="p-4 flex-1 flex flex-col">
                    <h3 className="font-extrabold text-slate-900 text-lg leading-snug"
                        style={{ fontFamily: "Outfit, sans-serif" }}>
                      {p.name}
                    </h3>
                    <p className="text-sm text-slate-600 mt-1 flex-1">{p.description}</p>
                    <div className="mt-3 flex items-center justify-between gap-3">
                      <div className="text-2xl font-black text-red-700"
                           data-testid={`product-price-${p.id}`}
                           style={{ fontFamily: "Outfit, sans-serif" }}>
                        {INR(p.price)}
                      </div>
                      <button
                        data-testid={`product-buy-${p.id}`}
                        onClick={() => buy(p)}
                        disabled={busy || !canAfford}
                        className={`inline-flex items-center gap-2 font-semibold text-sm rounded-lg px-4 py-2.5 transition-colors ${
                          canAfford
                            ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                            : "bg-slate-200 text-slate-500 cursor-not-allowed"
                        } disabled:opacity-70`}
                      >
                        {busy ? "Processing..." : canAfford ? (
                          <><ShoppingCart size={16} /> Purchase</>
                        ) : (
                          <><CheckCircle2 size={16} /> Add funds</>
                        )}
                      </button>
                    </div>
                    {!canAfford && (
                      <div className="mt-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1">
                        Fund your E-Wallet from <b>Fund Deposit</b> to purchase.
                      </div>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )}

        <div className="rounded-xl bg-blue-50 border border-blue-200 p-4 text-xs text-blue-900 leading-relaxed">
          <b>How it works:</b> Add funds via Fund Deposit → Admin verifies your deposit → your E-Wallet is credited → purchase a product from this page. Your ID becomes <b>Active</b> after your first purchase, and your sponsor gets a <b>₹200 Direct Income</b> automatically.
        </div>
      </main>
    </div>
  );
}
